import { jsPDF } from "jspdf";

/** Mesmo fator usado por `react-to-pdf` (mm → px a 96dpi). */
export const REACT_TO_PDF_PX_PER_MM = 3.77952755906;

/**
 * Altura de cada “página lógica” do PDF gerado por `react-to-pdf`, em pixels
 * de layout (equivalente ao que `getBoundingClientRect` usa no elemento capturado).
 *
 * A biblioteca fatia o canvas em faixas de altura = `pageAvailableHeightPx * scale * fit`;
 * em coordenadas de layout isso equivale a `pageAvailableHeightPx * fit` (o `scale` cancela).
 *
 * Para evolução futura com PDF textual (quebras semânticas), avaliar `@react-pdf/renderer`,
 * `pdfmake` ou impressão nativa (`window.print` + `@media print`).
 */
export function getPageSliceHeightLayoutPx({
  format = "a4",
  orientation = "portrait",
  margin = 0,
  captureWidthPx,
}) {
  const pdf = new jsPDF({ unit: "mm", format, orientation });
  const ps = pdf.internal.pageSize;
  const pageH =
    typeof ps.getHeight === "function" ? ps.getHeight() : ps.height;
  const pageW =
    typeof ps.getWidth === "function" ? ps.getWidth() : ps.width;
  const m = normalizeMarginsMm(margin);
  const pageAvailableWidthPx =
    (pageW - m.left - m.right) * REACT_TO_PDF_PX_PER_MM;
  const pageAvailableHeightPx =
    (pageH - m.top - m.bottom) * REACT_TO_PDF_PX_PER_MM;
  const w = Number(captureWidthPx) || 0;
  const fit =
    pageAvailableWidthPx > 0 && w > pageAvailableWidthPx + 0.5
      ? w / pageAvailableWidthPx
      : 1;
  return pageAvailableHeightPx * fit;
}

function normalizeMarginsMm(margin) {
  if (typeof margin === "number") {
    const n = Number(margin);
    return { top: n, right: n, bottom: n, left: n };
  }
  if (margin && typeof margin === "object") {
    return {
      top: Number(margin.top ?? 0),
      right: Number(margin.right ?? 0),
      bottom: Number(margin.bottom ?? 0),
      left: Number(margin.left ?? 0),
    };
  }
  return { top: 0, right: 0, bottom: 0, left: 0 };
}

/**
 * Retorna `margin-top` extra (px) a aplicar no rodapé para que o bloco não seja cortado
 * pelo fatiamento em canvas do `react-to-pdf`.
 */
export function computeFooterMarginTopToAvoidSlice(
  rootEl,
  footerEl,
  { format = "a4", orientation = "portrait", margin = 0 } = {}
) {
  const slice = getPageSliceHeightLayoutPx({
    format,
    orientation,
    margin,
    captureWidthPx: rootEl.getBoundingClientRect().width,
  });
  if (slice <= 0) return 0;

  const rootRect = rootEl.getBoundingClientRect();
  const footRect = footerEl.getBoundingClientRect();
  const top = footRect.top - rootRect.top;
  const bottom = footRect.bottom - rootRect.top;
  const blockHeight = bottom - top;

  if (blockHeight >= slice - 1e-3) {
    return 0;
  }

  const firstSlice = Math.floor((top + 1e-3) / slice);
  const lastSlice = Math.floor((bottom - 1e-3) / slice);
  if (firstSlice === lastSlice) return 0;

  const pushTo = (Math.floor(top / slice) + 1) * slice;
  return Math.max(0, pushTo - top);
}
